// src/stores/jobStore.js
import { defineStore } from "pinia";
import { ref, reactive } from "vue";
import svc from "@/services/jobs.service";
import { toast } from "@/utils/toast";

export const useJobStore = defineStore("jobs", () => {
  const items = ref([]);
  const loading = ref(false);
  const error = ref(null);

  const meta = ref({
    total: 0,
    per_page: 25,
    current_page: 1,
    last_page: 1,
  });

  const filters = reactive({
    q: "",
    status: "",
    role: "",
    from: "",
    to: "",
    page: 1,
    per_page: 25,
  });

  // for create/edit modal
  const form = reactive({
    id: null,
    job_code: "",
    title: "",
    description: "",
    category: "",
    job_type: "",
    job_level: "",
    experience_level: "",
    qualification: "",
    gender: "",
    min_salary: "",
    max_salary: "",
    salary_currency: "KES",
    salary_period: "month",
    expire_at: "",
    required_skills: "",
    address: "",
    city: "",
    state: "",
    country: "",
    zip_code: "",
    status: "open",
  });

  const saving = ref(false);

  function resetForm() {
    form.id = null;
    form.job_code = "";
    form.title = "";
    form.description = "";
    form.category = "";
    form.job_type = "";
    form.job_level = "";
    form.experience_level = "";
    form.qualification = "";
    form.gender = "";
    form.min_salary = "";
    form.max_salary = "";
    form.salary_currency = "KES";
    form.salary_period = "month";
    form.expire_at = "";
    form.required_skills = "";
    form.address = "";
    form.city = "";
    form.state = "";
    form.country = "";
    form.zip_code = "";
    form.status = "open";
  }

  async function fetch(params = {}) {
    loading.value = true;
    error.value = null;

    try {
      const response = await svc.list({
        ...filters,
        ...params,
        page: params.page ?? filters.page,
      });

      const payload = response.data;

      items.value = payload.data || [];
      meta.value = payload.meta || meta.value;
    } catch (e) {
      console.error(e);
      error.value = e;
      toast.error("Failed to load jobs");
    } finally {
      loading.value = false;
    }
  }

  async function fetchNextCode() {
    try {
      const res = await svc.nextCode();
      form.job_code = res.data.job_code;
    } catch (e) {
      console.error(e);
    }
  }

  function openCreate() {
    resetForm();
    fetchNextCode();
  }

  function openEdit(job) {
    resetForm();
    form.id = job.id;
    form.job_code = job.job_code;
    form.title = job.title;
    form.description = job.description;
    form.category = job.category;
    form.job_type = job.job_type;
    form.job_level = job.job_level;
    form.experience_level = job.experience_level;
    form.qualification = job.qualification;
    form.gender = job.gender;
    form.min_salary = job.min_salary;
    form.max_salary = job.max_salary;
    form.salary_currency = job.salary_currency || "KES";
    form.salary_period = job.salary_period || "month";
    form.expire_at = job.expire_at;
    form.required_skills = job.required_skills;
    form.address = job.address;
    form.city = job.city;
    form.state = job.state;
    form.country = job.country;
    form.zip_code = job.zip_code;
    form.status = job.status || "open";
  }

  async function save() {
    saving.value = true;
    error.value = null;

    const payload = {
      title: form.title,
      description: form.description,
      category: form.category,
      job_type: form.job_type,
      job_level: form.job_level,
      experience_level: form.experience_level,
      qualification: form.qualification,
      gender: form.gender,
      min_salary: form.min_salary || null,
      max_salary: form.max_salary || null,
      salary_currency: form.salary_currency,
      salary_period: form.salary_period,
      expire_at: form.expire_at || null,
      required_skills: form.required_skills,
      address: form.address,
      city: form.city,
      state: form.state,
      country: form.country,
      zip_code: form.zip_code,
      status: form.status,
    };

    try {
      let res;
      if (form.id) {
        res = await svc.update(form.id, payload);
        toast.success("Job updated");
      } else {
        res = await svc.create(payload);
        toast.success("Job created");
      }

      const job = res.data.data || res.data;

      // Update local list
      if (form.id) {
        const idx = items.value.findIndex((i) => i.id === job.id);
        if (idx !== -1) {
          items.value[idx] = job;
        }
      } else {
        items.value.unshift(job);
      }

      return job;
    } catch (e) {
      console.error(e);
      error.value = e;
      toast.error("Failed to save job");
      throw e;
    } finally {
      saving.value = false;
    }
  }

  async function destroy(id) {
    try {
      await svc.delete(id);
      items.value = items.value.filter((i) => i.id !== id);
      toast.success("Job deleted");
    } catch (e) {
      console.error(e);
      toast.error("Failed to delete job");
    }
  }

  async function bulkDestroy(ids) {
    try {
      await svc.bulkDelete(ids);
      items.value = items.value.filter((i) => !ids.includes(i.id));
      toast.success("Jobs deleted");
    } catch (e) {
      console.error(e);
      toast.error("Failed to delete jobs");
    }
  }

  return {
    items,
    loading,
    error,
    meta,
    filters,
    form,
    saving,
    fetch,
    openCreate,
    openEdit,
    save,
    destroy,
    bulkDestroy,
  };
});
